import React, { useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, User, Clock, ArrowLeft, Phone, FileText, ChevronRight, BookOpen, Eye } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import BlogFAQSection from '@/components/BlogFAQSection';
import BlogCTA from '@/components/BlogCTA';
import BreadcrumbNav from '@/components/BreadcrumbNav';
import ReadingProgress from '@/components/ReadingProgress';
import TableOfContents from '@/components/TableOfContents';
import ShareButtons from '@/components/ShareButtons';
import DOMPurify from 'dompurify';
import { getPostBySlug, getRelatedPosts, getBlogCategories, type BlogPost } from '@/data/blogPosts';

// Helper to get localized field
const getLocalizedField = (post: BlogPost, field: string, language: 'en' | 'es'): string => {
  const key = `${field}_${language}` as keyof BlogPost;
  return (post[key] as string) || '';
};

const getLocalizedArray = (post: BlogPost, field: string, language: 'en' | 'es'): string[] => {
  const key = `${field}_${language}` as keyof BlogPost;
  return (post[key] as string[]) || [];
};

// FAQ data by category
const categoryFAQs: Record<string, { en: { question: string; answer: string }[]; es: { question: string; answer: string }[] }> = {
  'Grass & Sod': {
    en: [
      { question: 'How long does sod last before installation?', answer: 'Fresh sod should be installed within 24-48 hours of delivery. We recommend installing the same day for best results. Keep sod in shade and water the edges if delayed.' },
      { question: 'How much sod do I need for my lawn?', answer: 'Measure your lawn length x width in feet to get square footage. Add 5-10% for cutting waste. Our team can help calculate exact amounts with a free quote.' },
      { question: 'Do you deliver sod to The Woodlands TX?', answer: 'Yes! We offer same-day sod delivery throughout The Woodlands, Magnolia, Conroe, Spring, Tomball, and all of Montgomery County. Call (936) 320-0763.' },
      { question: 'What is the best grass for shade in Texas?', answer: 'St. Augustine (Palmetto variety) is the best shade-tolerant grass for Texas. It handles up to 60% shade while remaining heat resistant.' },
      { question: 'When is the best time to install sod in Texas?', answer: 'Spring (April-May) and early fall (September-October) are ideal. Avoid extreme summer heat and winter dormancy periods for best establishment.' }
    ],
    es: [
      { question: '¿Cuánto tiempo dura el césped antes de la instalación?', answer: 'El césped fresco debe instalarse dentro de 24-48 horas de la entrega. Recomendamos instalar el mismo día para mejores resultados.' },
      { question: '¿Cuánto césped necesito para mi jardín?', answer: 'Mide el largo x ancho de tu jardín en pies para obtener los pies cuadrados. Agrega 5-10% para desperdicio de corte.' },
      { question: '¿Entregan césped a The Woodlands TX?', answer: '¡Sí! Ofrecemos entrega de césped el mismo día en The Woodlands, Magnolia, Conroe, Spring, Tomball y todo el Condado Montgomery. Llama (936) 320-0763.' },
      { question: '¿Cuál es el mejor pasto para sombra en Texas?', answer: 'St. Augustine (variedad Palmetto) es el mejor pasto tolerante a la sombra para Texas.' },
      { question: '¿Cuándo es el mejor momento para instalar césped en Texas?', answer: 'Primavera (Abril-Mayo) y principios de otoño (Septiembre-Octubre) son ideales.' }
    ]
  },
  'Mulch & Rock': {
    en: [
      { question: 'How much mulch do I need?', answer: 'For 3-inch depth coverage, divide your square footage by 108 to get cubic yards needed. Example: 500 sq ft ÷ 108 = 4.6 cubic yards.' },
      { question: 'How often should I replace mulch?', answer: 'Organic mulch should be refreshed every 1-2 years as it decomposes. Top off when it gets below 2 inches depth.' },
      { question: 'Is rock or mulch better for Texas?', answer: 'It depends on your goals. Rock is permanent and fire-resistant. Mulch improves soil and retains moisture. Many landscapes combine both.' },
      { question: 'Do you deliver bulk mulch?', answer: 'Yes! We deliver bulk mulch by the cubic yard throughout Montgomery County. Minimum delivery is 2 cubic yards.' }
    ],
    es: [
      { question: '¿Cuánto mantillo necesito?', answer: 'Para cobertura de 3 pulgadas de profundidad, divide tus pies cuadrados por 108 para obtener yardas cúbicas. Ejemplo: 500 pies cuadrados ÷ 108 = 4.6 yardas cúbicas.' },
      { question: '¿Con qué frecuencia debo reemplazar el mantillo?', answer: 'El mantillo orgánico debe refrescarse cada 1-2 años a medida que se descompone.' },
      { question: '¿Es mejor la roca o el mantillo para Texas?', answer: 'Depende de tus objetivos. La roca es permanente y resistente al fuego. El mantillo mejora el suelo y retiene humedad.' },
      { question: '¿Entregan mantillo a granel?', answer: '¡Sí! Entregamos mantillo a granel por yarda cúbica en todo Montgomery County. Mínimo de entrega es 2 yardas cúbicas.' }
    ]
  }
};

const BlogPostPage = () => {
  const { postSlug } = useParams();
  const { language } = useLanguage();
  const contentRef = useRef<HTMLDivElement>(null);

  // Get post from centralized data (try both slug fields for compatibility)
  let currentPost = getPostBySlug(postSlug || '', language);
  
  // Also try with the other language slug for cross-linking
  if (!currentPost) {
    currentPost = getPostBySlug(postSlug || '', language === 'en' ? 'es' : 'en');
  }

  // If post not found, redirect to blog index
  if (!currentPost) {
    return <Navigate to={`/${language}/blog`} replace />;
  }

  // Get localized content
  const title = getLocalizedField(currentPost, 'title', language);
  const excerpt = getLocalizedField(currentPost, 'excerpt', language);
  const content = getLocalizedField(currentPost, 'content', language);
  const category = getLocalizedField(currentPost, 'category', language);
  const metaTitle = getLocalizedField(currentPost, 'meta_title', language);
  const metaDescription = getLocalizedField(currentPost, 'meta_description', language);
  const tags = getLocalizedArray(currentPost, 'tags', language);
  const currentSlug = language === 'en' ? currentPost.slug_en : currentPost.slug_es;
  const articleUrl = `https://thegrassdepot.com/${language}/blog/${currentSlug}`;

  // Get related posts
  const relatedPosts = getRelatedPosts(currentPost.slug_en, 'en', 3);

  // Get FAQs based on category
  const categoryKey = currentPost.category_en;
  const faqs = categoryFAQs[categoryKey]?.[language] || categoryFAQs['Grass & Sod'][language];

  // Get all categories
  const categories = getBlogCategories(language);

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": excerpt,
    "image": currentPost.featured_image,
    "author": {
      "@type": "Person",
      "name": currentPost.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Grass Depot LLC",
      "logo": {
        "@type": "ImageObject",
        "url": "https://thegrassdepot.com/grass-depot-logo.png"
      }
    },
    "datePublished": currentPost.published_date,
    "dateModified": currentPost.published_date,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl
    },
    "inLanguage": language === 'en' ? 'en-US' : 'es-US',
    "keywords": tags.join(', ')
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { 
      name: 'Blog', 
      url: `/${language}/blog` 
    },
    { 
      name: category,
      url: `/${language}/blog?category=${category.toLowerCase().replace(/\s+/g, '-')}`
    },
    { 
      name: title.length > 50 ? title.substring(0, 50) + '...' : title
    }
  ];

  // Calculate word count for estimated reading
  const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;

  return (
    <div className="min-h-screen flex flex-col">
      <ReadingProgress />
      
      <SEO 
        title={metaTitle || title}
        description={metaDescription || excerpt}
        keywords={tags.join(', ')}
        canonical={articleUrl}
        structuredData={structuredData}
      />

      <Header />
      
      <main className="flex-grow">
        {/* Enhanced Hero Section */}
        <section className="relative min-h-[60vh] lg:min-h-[70vh] flex items-end overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center scale-105 animate-slow-zoom"
            style={{ 
              backgroundImage: `url(${currentPost.featured_image})`,
              animation: 'slowZoom 20s ease-out forwards'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
          
          <div className="relative z-10 container mx-auto px-4 pb-12 lg:pb-16">
            {/* Breadcrumb on hero */}
            <div className="mb-6">
              <BreadcrumbNav items={breadcrumbItems} className="text-white/70" />
            </div>

            {/* Category Badge */}
            <Badge className="mb-4 bg-primary text-primary-foreground px-4 py-1.5 text-sm font-medium">
              {category}
            </Badge>
            
            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 max-w-4xl leading-tight">
              {title}
            </h1>
            
            {/* Excerpt */}
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mb-8 leading-relaxed">
              {excerpt}
            </p>
            
            {/* Meta Info Row */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-white/80">
              {/* Author */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/30 border-2 border-white/30 flex items-center justify-center text-white font-semibold">
                  {currentPost.author.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <span className="block text-white font-medium">{currentPost.author}</span>
                  <span className="text-sm text-white/60">
                    {language === 'en' ? 'Landscaping Expert' : 'Experto en Paisajismo'}
                  </span>
                </div>
              </div>

              <div className="hidden md:block w-px h-8 bg-white/20" />
              
              {/* Date */}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>
                  {new Date(currentPost.published_date).toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              
              {/* Reading Time */}
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{currentPost.read_time}</span>
              </div>

              {/* Word Count */}
              <div className="hidden lg:flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                <span>{wordCount.toLocaleString()} {language === 'en' ? 'words' : 'palabras'}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="container mx-auto px-4 py-12 lg:py-16">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Main Content */}
            <div className="lg:col-span-8">
              {/* Back to Blog */}
              <Link 
                to={`/${language}/blog`}
                className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-8 transition-colors group"
              >
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium">{language === 'en' ? 'Back to Blog' : 'Volver al Blog'}</span>
              </Link>

              {/* Article Body with Enhanced Styling */}
              <div 
                ref={contentRef}
                className="blog-content"
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(content) 
                }}
              />

              {/* Share Section after content */}
              <div className="mt-12 pt-8 border-t border-border">
                <ShareButtons title={title} url={articleUrl} />
              </div>

              {/* Mid-Article CTA */}
              <div className="mt-12">
                <BlogCTA variant="mid-article" />
              </div>

              {/* FAQ Section */}
              {faqs && faqs.length > 0 && (
                <div className="mt-16">
                  <BlogFAQSection faqs={faqs} />
                </div>
              )}

              {/* Tags Section */}
              <div className="mt-12 pt-8 border-t border-border">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-6 bg-primary rounded-full" />
                  {language === 'en' ? 'Related Topics' : 'Temas Relacionados'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary"
                      className="px-4 py-2 text-sm hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Enhanced Author Bio */}
              <div className="mt-12 p-8 bg-gradient-to-br from-muted/80 to-muted/40 rounded-2xl border border-border">
                <div className="flex flex-col sm:flex-row items-start gap-6">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-primary-foreground font-bold text-2xl shadow-lg">
                    {currentPost.author.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-xl">{currentPost.author}</h3>
                      <Badge variant="outline" className="text-xs">
                        {language === 'en' ? 'Expert Author' : 'Autor Experto'}
                      </Badge>
                    </div>
                    <p className="text-primary text-sm font-medium mb-3">
                      {language === 'en' ? 'Landscaping & Lawn Care Specialist' : 'Especialista en Paisajismo y Cuidado del Césped'}
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      {language === 'en' 
                        ? 'With years of experience serving homeowners and businesses in Montgomery County, Texas, our expert team provides trusted advice on lawn care, material selection, and landscape design. We\'re committed to helping you create beautiful outdoor spaces.'
                        : 'Con años de experiencia sirviendo a propietarios y negocios en el Condado Montgomery, Texas, nuestro equipo experto proporciona asesoramiento confiable sobre cuidado del césped, selección de materiales y diseño de paisajes. Estamos comprometidos a ayudarte a crear hermosos espacios exteriores.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Final CTA */}
              <div className="mt-12">
                <BlogCTA variant="full" />
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              {/* Sticky Sidebar */}
              <div className="lg:sticky lg:top-8 space-y-6">
                {/* Table of Contents */}
                <TableOfContents contentRef={contentRef} />

                {/* Quick Contact */}
                <Card className="overflow-hidden">
                  <div className="bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
                    <CardTitle className="text-lg mb-2">
                      {language === 'en' ? 'Need Expert Help?' : '¿Necesitas Ayuda Experta?'}
                    </CardTitle>
                    <p className="text-sm opacity-90">
                      {language === 'en' 
                        ? 'Get a free quote for your landscaping project today.'
                        : 'Obtén una cotización gratis para tu proyecto hoy.'}
                    </p>
                  </div>
                  <CardContent className="p-6 space-y-3">
                    <Button asChild className="w-full" size="lg">
                      <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
                        <FileText className="mr-2 h-4 w-4" />
                        {language === 'en' ? 'Get Free Quote' : 'Cotiza Gratis'}
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full" size="lg">
                      <a href="tel:+19363200763">
                        <Phone className="mr-2 h-4 w-4" />
                        (936) 320-0763
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                {/* Related Articles */}
                {relatedPosts.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Eye className="h-4 w-4 text-primary" />
                        {language === 'en' ? 'You Might Also Like' : 'También Te Puede Gustar'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {relatedPosts.map((post) => {
                        const postTitle = getLocalizedField(post, 'title', language);
                        const postSlug = language === 'en' ? post.slug_en : post.slug_es;
                        return (
                          <Link 
                            key={post.slug_en}
                            to={`/${language}/blog/${postSlug}`}
                            className="block group"
                          >
                            <div className="flex gap-4">
                              <img 
                                src={post.featured_image}
                                alt={postTitle}
                                className="w-24 h-20 object-cover rounded-lg group-hover:scale-105 transition-transform"
                              />
                              <div className="flex-1 flex flex-col justify-center">
                                <h4 className="text-sm font-medium group-hover:text-primary transition-colors line-clamp-2 mb-1">
                                  {postTitle}
                                </h4>
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {post.read_time}
                                </span>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}

                {/* Categories */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {language === 'en' ? 'Explore Categories' : 'Explorar Categorías'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {categories.map((cat) => (
                        <Link 
                          key={cat.name}
                          to={`/${language}/blog?category=${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors group"
                        >
                          <span className="text-sm font-medium group-hover:text-primary transition-colors">{cat.name}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">{cat.count}</Badge>
                            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Related Products */}
                {currentPost.related_products && currentPost.related_products.length > 0 && (
                  <Card className="border-primary/20">
                    <CardHeader className="bg-primary/5">
                      <CardTitle className="text-lg">
                        {language === 'en' ? 'Featured Products' : 'Productos Destacados'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="space-y-2">
                        {currentPost.related_products.map((product: string) => (
                          <Link 
                            key={product}
                            to={`/${language}/grass`}
                            className="flex items-center justify-between p-3 rounded-lg hover:bg-primary/5 transition-colors group border border-transparent hover:border-primary/20"
                          >
                            <span className="text-sm capitalize font-medium">{product.replace(/-/g, ' ')}</span>
                            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </aside>
          </div>
        </article>
      </main>

      {/* Mobile Sticky CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-md border-t p-4 z-50 shadow-lg">
        <div className="flex gap-3">
          <Button asChild className="flex-1 h-12 text-base font-semibold">
            <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
              <FileText className="mr-2 h-5 w-5" />
              {language === 'en' ? 'Get Quote' : 'Cotizar'}
            </Link>
          </Button>
          <Button asChild variant="outline" className="h-12 w-12 p-0">
            <a href="tel:+19363200763" aria-label="Call us">
              <Phone className="h-5 w-5" />
            </a>
          </Button>
        </div>
      </div>

      <Footer />

      {/* Animation keyframes */}
      <style>{`
        @keyframes slowZoom {
          from { transform: scale(1.05); }
          to { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default BlogPostPage;
